'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowBackIos, ArrowForwardIos, Cancel } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link } from '@prismo-io/design-system';
import type {
  LanguageCodeT,
  ToolsCollaboratorsAnalyzeTalentsT,
} from '@prismo-io/schemas';
import { useIsStuck } from '@prismo-io/utils/client/use-is-stuck';
import { useAction } from 'next-safe-action/hooks';
import type { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AutoScrollToFocusElement } from '../focus-element/auto-scroll';
import { CategoryItem } from './category-item';
import { UpdateConfigAction } from './update-config';

type Props = {
  logoSrc: StaticImport;
  data: ToolsCollaboratorsAnalyzeTalentsT;
};

export const CheckList: FC<Props> = (props) => {
  const { logoSrc, data } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const [list, setList] = useState<ToolsCollaboratorsAnalyzeTalentsT['config']>(
    () => data.config
  );

  const steps = useMemo(() => list.flatMap((step) => step.steps), [list]);

  const [stepIdx, setStepIdx] = useState<number>(() => {
    const idx = data.config
      .flatMap((step) => step.steps)
      .findIndex((step) => !step.checked);

    if (idx === -1) {
      return 0;
    }

    return idx;
  });

  const step = useMemo(() => steps[stepIdx], [steps, stepIdx]);

  const { i18n } = useLingui();

  const hasPrevious = useMemo(() => stepIdx > 0, [stepIdx]);

  const hasNext = useMemo(() => stepIdx < steps.length - 1, [stepIdx]);

  const onPrev = useCallback(
    () =>
      setStepIdx((prev) => {
        const tmp = prev - 1;

        return tmp < 0 ? 0 : tmp;
      }),
    [setStepIdx]
  );

  const onNext = useCallback(
    () => setStepIdx((prev) => prev + 1),
    [setStepIdx]
  );

  const { execute } = useAction(UpdateConfigAction, {
    onSuccess: async ({ data }) => {
      if (data) {
        setList(data.config);
      }
    },
  });

  const onCheck = useCallback(
    (_event: unknown, checked: boolean) => {
      const newList = JSON.parse(JSON.stringify(list));
      for (const category of newList) {
        for (const cs of category.steps) {
          if (cs.id === step.id) {
            cs.checked = checked;
          }
        }
      }

      execute({
        id: data.id,
        config: newList,
      });
    },
    [step, list, execute, data.id]
  );

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const onClose = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('mode');
    params.delete('focus');
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [router, pathname, searchParams]);

  const [marginTop, setMarginTop] = useState(() =>
    ref.current ? ref.current.getBoundingClientRect().height * 1.1 : 0
  );

  useEffect(() => {
    setMarginTop(
      ref.current ? ref.current.getBoundingClientRect().height * 1.1 : 0
    );
  }, [ref.current]);

  useEffect(() => {
    if (step?.id && !!marginTop) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('focus', step.id.trim());
      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, [step?.id, marginTop]);

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const isStuck = useIsStuck(ref.current, isMobile ? 10 : marginTop / 2);

  return (
    <>
      <AutoScrollToFocusElement offsetTop={marginTop} />
      <Stack
        ref={ref}
        gap={2}
        marginTop={isMobile ? 2 : undefined}
        padding={2}
        bgcolor="background.primary"
        position="sticky"
        top={0}
        width="100%"
        zIndex={998}
      >
        <Box
          component="div"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            transitionProperty: 'all',
            transitionTimingFunction: 'ease-in-out',
            transitionDuration: '300ms',
          }}
          boxShadow={(theme: Theme) =>
            isStuck
              ? theme.shadows[3]
              : '0px 3px 3px -2px rgb(255 0 0 / 0%), 0px 3px 4px 0px rgb(255 0 0 / 0%), 0px 1px 8px 0px rgb(255 0 0 / 0%)'
          }
        />
        <Stack direction="row" gap={2} alignItems="center">
          <Box
            component="div"
            position="relative"
            width="3rem"
            height="3rem"
            sx={{ display: { none: 'none', md: 'block' } }}
          >
            <Image
              src={logoSrc}
              alt=""
              style={{ objectFit: 'contain', objectPosition: 'center' }}
              fill
            />
          </Box>

          <Typography variant="h2" display={{ none: 'none', md: 'block' }}>
            Aide à l'analyse des résultats
          </Typography>

          <Box
            component="div"
            sx={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: '1fr',
              gap: '1rem',
            }}
            width={{ none: '75%', md: 'auto' }}
            margin={{ none: 'auto', md: 'unset' }}
          >
            {list.map((category, idx) => (
              <CategoryItem
                key={category.title.fr}
                category={category}
                activeStepId={step.id}
              />
            ))}
          </Box>
        </Stack>
        <Stack
          position="relative"
          marginX={{ noe: '1rem', md: '4rem' }}
          gap={2}
        >
          <Typography
            fontWeight="bold"
            fontStyle="italic"
            display={{ none: 'none', md: 'block' }}
          >
            <Trans>
              Pour télécharger la grille d'évaluation,{' '}
              <Link
                href="https://hubs.ly/Q02y5gPT0"
                target="_blank"
                color="inherit"
              >
                cliquez ici
              </Link>
            </Trans>
          </Typography>
          <FormControlLabel
            checked={step.checked}
            onChange={onCheck}
            sx={{
              margin: 0,
            }}
            control={
              <Checkbox
                size={isMobile ? 'small' : 'medium'}
                color="primary"
                sx={{
                  padding: 0,
                  marginRight: 1,
                }}
              />
            }
            label={
              <Typography
                fontSize={{ none: '9px', md: '14px' }}
                dangerouslySetInnerHTML={{
                  __html: step.label[i18n.locale as LanguageCodeT] as string,
                }}
              />
            }
          />
        </Stack>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent={{ none: 'space-between', md: 'center' }}
        >
          {hasPrevious ? (
            <Button
              variant={isMobile ? 'text' : 'outlined'}
              size={isMobile ? 'small' : 'medium'}
              onClick={onPrev}
              startIcon={isMobile ? <ArrowBackIos /> : undefined}
            >
              Précédent
            </Button>
          ) : isMobile ? (
            <Button
              component="a"
              href="https://hubs.ly/Q02y5gPT0"
              target="_blank"
              size={isMobile ? 'small' : 'medium'}
            >
              <Trans>Téléchargez la grille d'évaluation</Trans>
            </Button>
          ) : null}
          {hasNext ? (
            <Button
              variant={isMobile ? 'text' : 'outlined'}
              size={isMobile ? 'small' : 'medium'}
              onClick={onNext}
              endIcon={isMobile ? <ArrowForwardIos /> : undefined}
            >
              Suivant
            </Button>
          ) : (
            <Button
              component="a"
              variant="outlined"
              href="https://hubs.ly/Q02y5gPT0"
              target="_blank"
              size={isMobile ? 'small' : 'medium'}
            >
              <Trans>Téléchargez la grille d'évaluation</Trans>
            </Button>
          )}
        </Stack>
        <Box component="div" position="absolute" top={1} right={1}>
          <IconButton onClick={onClose} color="primary">
            <Cancel />
          </IconButton>
        </Box>
      </Stack>
    </>
  );
};
